/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Typography
} from '@mui/material';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddIcon from '@mui/icons-material/Add';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';
import { Bone } from '../../../../../../components/ui/DashboardSkeletons';
import HelpEdit from './HelpEdit';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { articleActions, fetchAllArticles, IArticleData } from '../../../../../../redux/slices/article/getAllArticleSlice';
import { getUserDetails } from '../../../../../../utils/orgName';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import RenderHTML from '../../../../../../components/RenderHTML';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import { buttonDisabledSx } from '../../../../../../components/ui/Buttons/buttonStyles';
import { API } from '../../../../../../api';
import ChatAvatarComponent from '../../../chat/ChatAvatarComponent';
import { updateInsightDataByArticleId } from '../../../../components/common';
import AddEditContent from '../../../Help/components/AddEditContent';
import ROLES from '../../../../../../utils/roles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';

export type Article = IArticleData;

const ContentSettings = () => {
  const { groupId: grpId, orgId, location: resolvedLocation, isPlatformAdmin } = useProgramContext();
  const dispatch = useAppDispatch();
  const articleState = useAppSelector((state) => state.allArticle);
  const articles = articleState.data?.ArticleList || [];

  const groupTopics: string[] = useAppSelector((state) => state.groupDetails.data?.topics) || [];
  // The org parent group id (orgDetails.data.grpId). When the page has no real program
  // group — empty, "admin", or the org parent group — resources are org-wide and must
  // use the org-scoped endpoints, not the group-scoped ones.
  const orgParentGroupId = useAppSelector((state) => state.orgDetails.data?.grpId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [action, setAction] = useState<'Add' | 'Edit'>('Add');
  const [selectedArticleData, setSelectedArticleData] = useState<IArticleData | undefined>(undefined);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<IArticleData | null>(null);
  const [removeTarget, setRemoveTarget] = useState<IArticleData | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  // A phone lists the resources as cards rather than a wide table, and opens the preview full screen.
  const isMobileApp = useIsMobileApp();

  const refreshArticles = () => {
    dispatch(fetchAllArticles({ orgId, groupId: grpId, helpSettings: false, useCache: false, role: 'admin', location: resolvedLocation }));
    if (!orgId && !grpId && resolvedLocation) {
      dispatch(fetchAllArticles({ orgId: '', groupId: '', helpSettings: false, useCache: false, location: resolvedLocation }));
    }
  };

  useEffect(() => {
    refreshArticles();
  }, [dispatch, grpId, orgId]);

  useEffect(() => {
    if (articles.length && !selectedId) {
      setSelectedId(articles[0].articleId);
      setSelectedArticleData(articles[0]);
    }
  }, [articles, selectedId]);

  const selectedArticle = useMemo(() => articles.find((a) => a.articleId === selectedId), [articles, selectedId]);

  const openAddModal = () => {
    setAction('Add');
    setSelectedArticleData(undefined);
    setSelectedId(null);
    setOpenForm(true);
  };

  const openEditModal = async (article: IArticleData) => {
    setAction('Edit');
    setSelectedId(article.articleId);
    setSelectedArticleData(undefined);
    try {
      const response = await API.getAllArticleById({
        orgId,
        articleId: article.articleId,
        groupId: grpId,
        isContentSettings: true,
        articleLocation: article.location || localStorage.getItem('orgLocation') || 'us'
      });
      if (response && response.status === 200) {
        const apiArticle = response.data.article || {};
        const resolvedArticleId = apiArticle.articleId || apiArticle._id || article.articleId;
        setSelectedId(resolvedArticleId);
        setSelectedArticleData({ ...apiArticle, articleId: resolvedArticleId });
        setOpenForm(true);
      }
    } catch (error) {
      toast.error('The resource could not be loaded. Refresh the page and try again.');
    } finally {
      // no-op
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedArticleData(undefined);
    setSelectedId(null);
    setAction('Add');
  };

  const handleArticleCreated = () => {
    refreshArticles();
  };

  const handleOpenRemove = (article: IArticleData) => {
    setRemoveTarget(article);
  };

  const handleCloseRemove = () => {
    setRemoveTarget(null);
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    // Org-wide resource: no real program group (empty, "admin", or the org parent group).
    // The group-scoped deleteArticle rejects it ("Group not found") and can't reach a
    // createdGroup:[] resource, so delete via the org endpoint.
    const isOrgLevel = !grpId || grpId === 'admin' || grpId === orgParentGroupId;
    const targetLocation = removeTarget.location || resolvedLocation;
    try {
      const response = isOrgLevel
        ? await API.DeleteOrgArticle({
            authId: orgId,
            articleId: removeTarget.articleId,
            location: targetLocation
          })
        : await API.DeleteArticle({
            orgId,
            articleId: removeTarget.articleId,
            groupId: grpId
          });
      if (response.status === 200) {
        dispatch(articleActions.deleteArticle(removeTarget.articleId));
        refreshArticles();
        handleCloseRemove();
      }
    } catch (error) {
      toast.error('The resource could not be removed. Try again.');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleOpenPreview = async (articleId: string, location?: string) => {
    setOpenPreview(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const response = await API.getAllArticleById({
        orgId,
        articleId,
        groupId: grpId,
        isContentSettings: true,
        articleLocation: location ? location : ''
      });
      if (response && response.status === 200) {
        setPreviewData(response.data.article);
      }
    } catch (error) {
      toast.error('The resource could not be loaded. Refresh the page and try again.');
    } finally {
      setPreviewLoading(false);
    }
    updateInsightDataByArticleId(orgId, articleId, location);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
  };

  const handleToggleVisibility = async (article: IArticleData, checked: boolean) => {
    dispatch(articleActions.updateArticle({ ...article, isPublished: checked }));
    const formData = new FormData();
    formData.append('isPublished', checked.toString());
    // if (article.coverImageUrl) {
    //   formData.append('coverImage', article.coverImageUrl);
    // }
    // if (article.fileUrl) {
    //   formData.append('type', article.type);
    //   formData.append('file', article.fileUrl);
    // }
    const targetLocation = article.location || localStorage.getItem('orgLocation') || 'us';
    // Org-wide resource: the group-scoped updateArticle rejects the empty/parent group,
    // so publish/unpublish must go through the org endpoint.
    const isOrgLevel = !grpId || grpId === 'admin' || grpId === orgParentGroupId;
    try {
      const response = isOrgLevel
        ? await API.EditOrgArticle({
            authId: orgId,
            articleId: article.articleId,
            data: formData,
            location: targetLocation
          })
        : await API.editArticle({
            articleId: article.articleId,
            data: formData,
            orgId,
            groupId: grpId,
            helpSettings: true,
            location: targetLocation
          });
      if (response.status === 200) {
        dispatch(articleActions.updateArticle(response.data.updatedArticle));
        refreshArticles();
      } else {
        toast.error('The resource could not be published. Try again.');
        refreshArticles();
      }
    } catch (error) {
      toast.error('The resource could not be updated. Try again.');
      refreshArticles();
    }
  };

  const previewTags = previewData?.tags || [];
  const previewTagDisplay = previewTags.slice(0, 2);
  const previewExtraCount = previewTags.length - previewTagDisplay.length;
  const readTimeLabel = previewData?.readTime ? `${previewData.readTime} read` : '';
  const fileName = previewData?.fileUrl?.split('/').pop() || 'Attachment';
  const fileExt = fileName.includes('.') ? fileName.split('.').pop() || '' : '';
  const fileTypeLabel = fileExt ? fileExt.toUpperCase() : previewData?.type || '';

  const renderHeaderTabs = () => {
    const renderTab = (label: string, selected?: boolean, disabled?: boolean) => (
      <Box
        key={label}
        sx={{
          px: 2.3,
          py: 1.1,
          background: selected ? '#444444' : disabled ? '#151515' : '#2A2A2A',
          color: selected ? '#FDFDFD' : disabled ? '#868686' : '#B6B6B6',
          fontSize: 12,
          fontWeight: selected ? 700 : 400,
          opacity: disabled ? 0.5 : 1
        }}
      >
        {label}
      </Box>
    );

    return (
      <Box sx={{ ml: 'auto', display: 'inline-flex', borderRadius: 1, overflow: 'hidden' }}>
        {renderTab('Design', false, true)}
        {renderTab('Configuration', false, false)}
        {renderTab('Resources', true, false)}
      </Box>
    );
  };

  const EmptyState = () => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ width: 400, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ color: '#B6B6B6', fontSize: 16, fontWeight: 600 }}>
          You have no resources yet.
        </Typography>
        <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Upload resources to continue.</Typography>
      </Box>
      <Button
        onClick={openAddModal}
        sx={{
          px: 1.8,
          py: 1,
          borderRadius: 1,
          background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
          color: '#FDFDFD',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600
        }}
      >
        Add Resource
      </Button>
    </Box>
  );

  const ArticleList = () => (
    <Box
      sx={{
        width: 320,
        borderRight: '1px solid #2A2A2A',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        minHeight: 0
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 600, flex: 1 }}>Resources</Typography>
        <Button
          onClick={openAddModal}
          sx={{
            minWidth: 0,
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'none',
            color: '#FDFDFD',
            p: 0,
            fontSize: 18,
            lineHeight: 1,
            '&:hover': { opacity: 0.9 }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </Button>
        <Box
          sx={{
            width: 30,
            height: 30,
            bgcolor: '#1C1C1C',
            borderRadius: '50%',
            opacity: 0.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#868686',
            pointerEvents: 'none'
          }}
        >
          <FilterListIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>All ({articles.length})</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {articles.map((article) => {
          const isActive = article.articleId === selectedId;
          return (
            <Box
              key={article.articleId}
              onClick={() => {
                setSelectedId(article.articleId);
                setSelectedArticleData(article);
              }}
              sx={{
                px: 3,
                py: 2.5,
                display: 'flex',
                gap: 1.5,
                cursor: 'pointer',
                bgcolor: isActive ? '#151515' : 'transparent',
                borderBottom: '1px solid #2A2A2A'
              }}
            >
              <Box
                sx={{
                  width: 114,
                  height: 68,
                  borderRadius: 1,
                  backgroundImage: `url(${article.coverImageUrl || 'https://placehold.co/114x68'})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 500, lineHeight: 1.3 }}>
                  {article.articleName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      bgcolor: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <ThumbUpOffAltIcon sx={{ fontSize: 14, color: '#B6B6B6' }} />
                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>{article.upVotes}</Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      bgcolor: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <ThumbDownOffAltIcon sx={{ fontSize: 14, color: '#B6B6B6' }} />
                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>
                      {article.downVotes}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      bgcolor: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 14, color: '#B6B6B6' }} />
                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>{article.views}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const ArticleDetail = () =>
    selectedArticle ? (
      <Box
        sx={{
          flex: 1,
          px: 3,
          py: 3.5,
          bgcolor: '#151515',
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[selectedArticle.articleViewableTo, ...(selectedArticle.articleViewableUserType || [])]
                .filter(Boolean)
                .map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      px: 1,
                      py: 0.6,
                      bgcolor: '#1C1C1C',
                      color: '#FDFDFD',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: 'uppercase'
                    }}
                  >
                    {tag}
                  </Box>
                ))}
            </Box>

            <Typography sx={{ color: '#FDFDFD', fontSize: 30, fontWeight: 700 }}>
              {selectedArticle.articleName}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, color: '#B6B6B6', fontSize: 12 }}>
              <span>{selectedArticle.createdAt ? new Date(selectedArticle.createdAt * 1000).toDateString() : ''}</span>
              <span>{selectedArticle.readTime}</span>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
              {(selectedArticle.tags || []).map((t) => (
                <Box
                  key={t}
                  sx={{
                    px: 1,
                    py: 0.4,
                    bgcolor: '#2A2A2A',
                    borderRadius: 20,
                    color: '#FDFDFD',
                    fontSize: 12
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                width: 300,
                px: 1.2,
                py: 1.1,
                borderRadius: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: 'transparent'
              }}
            >
              <ChatAvatarComponent
                image={undefined}
                firstLetter={selectedArticle.authorName?.charAt(0) || ''}
                width="30px"
                height="30px"
                type="noStatus"
              />
              <Box
                sx={{
                  color: '#FDFDFD',
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: '17.6px'
                }}
              >
                {selectedArticle.authorName}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#444' }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, color: 'white !important' }}>
            <RenderHTML htmlContent={selectedArticle.articleDescription || ''} />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 0.5,
                  bgcolor: '#1C1C1C',
                  border: '1px solid #2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <ThumbUpOffAltIcon sx={{ fontSize: 18, color: '#B6B6B6' }} />
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>
                  {selectedArticle.upVotes}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 0.5,
                  bgcolor: '#1C1C1C',
                  border: '1px solid #2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <ThumbDownOffAltIcon sx={{ fontSize: 18, color: '#B6B6B6' }} />
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>
                  {selectedArticle.downVotes}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 0.5,
                  bgcolor: '#1C1C1C',
                  border: '1px solid #2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#B6B6B6' }} />
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>
                  {selectedArticle.views}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#444' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Was this content helpful?</Typography>
            <Box
              sx={{
                px: 1,
                py: 0.6,
                borderRadius: 0.5,
                bgcolor: '#1C1C1C',
                border: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ThumbUpOffAltIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
            <Box
              sx={{
                px: 1,
                py: 0.6,
                borderRadius: 0.5,
                bgcolor: '#1C1C1C',
                border: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ThumbDownOffAltIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
          </Box>
        </Box>
      </Box>
    ) : null;

  interface ItemListProps {
    name?: string;
    upVotes?: string | number;
    downVotes?: string | number;
    contentType?: string;
    readTime?: string | number;
    tags?: string[];
    enabled?: boolean;
    onToggle?: (checked: boolean) => void;
    onPreview?: () => void;
    onEdit?: () => void;
    onRemove?: () => void;
  }
  const ItemList = (item: ItemListProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleCloseMenu = () => {
      setAnchorEl(null);
    };
    const handleMenuAction = (action?: () => void) => {
      handleCloseMenu();
      action?.();
    };
    const visibilitySwitch = (
      <Switch
        checked={Boolean(item.enabled)}
        onChange={(event) => item.onToggle?.(event.target.checked)}
        size="small"
        sx={{
          width: 36,
          height: 16,
          p: 0,
          '& .MuiSwitch-switchBase': {
            p: 0.1,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: 'var(--Stroke-accent, #F79420)',
              '& + .MuiSwitch-track': {
                backgroundColor: 'var(--Fill-switch-track, #1C1C1C)',
                border: '1px solid var(--Stroke-switch-front, #2A2A2A)'
              }
            }
          },
          '& .MuiSwitch-thumb': {
            width: 14,
            height: 14,
            boxShadow: 'none',
            backgroundColor: 'var(--Stroke-accent, #F79420)'
          },
          '& .MuiSwitch-track': {
            borderRadius: 79,
            backgroundColor: 'var(--Fill-switch-track, #1C1C1C)',
            border: '1px solid var(--Stroke-switch-front, #2A2A2A)',
            opacity: 1
          }
        }}
      />
    );
    const menuButton = (
      <IconButton
        size="small"
        onClick={handleOpenMenu}
        aria-label={`Actions for ${item.name ?? 'this resource'}`}
        sx={{
          width: 32,
          height: 32,
          color: 'var(--Stroke-solid-white, #FDFDFD)',
          [MOBILE_SX]: { width: 40, height: 40, mr: '-8px' }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    );
    const actionsMenu = (
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: 'var(--Fill-background, #151515)',
            border: '1px solid var(--Stroke-Border-border, #2A2A2A)',
            borderRadius: '5px',
            mt: 1,
            px: 1.75,
            py: 1.25
          }
        }}
        MenuListProps={{
          sx: {
            p: 0,
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            // A phone: each action a full tap target.
            [MOBILE_SX]: { gap: 0, '& .MuiMenuItem-root': { minHeight: 40, minWidth: 140, fontSize: 14 } }
          }
        }}
      >
        <MenuItem
          disableGutters
          onClick={() => handleMenuAction(item.onPreview)}
          sx={{
            px: 0,
            py: 0,
            minHeight: 'auto',
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'Inter',
            '&:hover': { backgroundColor: 'transparent' }
          }}
        >
          Preview
        </MenuItem>
        <MenuItem
          disableGutters
          onClick={() => handleMenuAction(item.onEdit)}
          sx={{
            px: 0,
            py: 0,
            minHeight: 'auto',
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'Inter',
            '&:hover': { backgroundColor: 'transparent' }
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          disableGutters
          onClick={() => handleMenuAction(item.onRemove)}
          sx={{
            px: 0,
            py: 0,
            minHeight: 'auto',
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            color: 'var(--UI-Error, #FF4D4F)',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'Inter',
            '&:hover': { backgroundColor: 'transparent' }
          }}
        >
          Remove
        </MenuItem>
      </Menu>
    );
    // A phone: one card per resource; its name whole, the figures and tags beneath, every action a tap.
    if (isMobileApp) {
      const metaSx = { color: '#B6B6B6', fontSize: 12.5, fontWeight: 500, fontFamily: 'Inter', lineHeight: '18px' };
      return (
        <Box
          sx={{
            px: '16px',
            py: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            borderBottom: '1px solid var(--Stroke-table-primary, #2A2A2A)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'Inter',
                lineHeight: '20px',
                overflowWrap: 'anywhere'
              }}
            >
              {item.name}
            </Typography>
            {/* The label is the switch's 40px tap target: a tap anywhere on it toggles the switch. */}
            <Box
              component="label"
              sx={{ height: 40, minWidth: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              {visibilitySwitch}
            </Box>
            {menuButton}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px' }}>
            {item.contentType && (
              <Typography sx={{ ...metaSx, color: '#FDFDFD', fontWeight: 600, textTransform: 'uppercase' }}>
                {item.contentType}
              </Typography>
            )}
            {item.readTime ? <Typography sx={metaSx}>{item.readTime}</Typography> : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }} aria-label={`${item.upVotes ?? 0} upvotes`}>
              <ThumbUpOffAltIcon sx={{ fontSize: 15, color: '#B6B6B6' }} />
              <Typography sx={metaSx}>{item.upVotes ?? 0}</Typography>
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              aria-label={`${item.downVotes ?? 0} downvotes`}
            >
              <ThumbDownOffAltIcon sx={{ fontSize: 15, color: '#B6B6B6' }} />
              <Typography sx={metaSx}>{item.downVotes ?? 0}</Typography>
            </Box>
          </Box>
          {(item.tags || []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(item.tags || []).map((tag) => (
                <Box
                  key={tag}
                  sx={{
                    px: 1,
                    py: '4px',
                    bgcolor: 'var(--Fill-Tag-social-tag, #2A2A2A)',
                    borderRadius: 20,
                    border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontSize: 12,
                    fontFamily: 'Inter',
                    lineHeight: '14px'
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          )}
          {actionsMenu}
        </Box>
      );
    }
    return (
      <Box
        sx={{
          alignSelf: 'stretch',
          px: 3,
          py: 1.75,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 3.5,
          width: '100%'
        }}
      >
        {visibilitySwitch}
        <Box sx={{ flex: 1, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
          <Typography
            sx={{
              width: '100%',
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'Inter',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.name}
          </Typography>
        </Box>
        <Box sx={{ width: 80, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
          <Typography
            sx={{
              width: '100%',
              textAlign: 'center',
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'Inter',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.upVotes}
          </Typography>
        </Box>
        <Box sx={{ width: 80, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
          <Typography
            sx={{
              width: '100%',
              textAlign: 'center',
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'Inter',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.downVotes}
          </Typography>
        </Box>
        <Box sx={{ width: 192, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
          <Typography
            sx={{
              width: '100%',
              textAlign: 'center',
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'Inter',
              textTransform: 'uppercase',
              lineHeight: '24px',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.contentType}
          </Typography>
        </Box>
        <Box sx={{ width: 112, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
          <Typography
            sx={{
              width: '100%',
              textAlign: 'center',
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'Inter',
              lineHeight: '24px',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.readTime}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 320,
            maxWidth: 320,
            minWidth: 0,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '5px',
            overflow: 'hidden'
          }}
        >
          {(() => {
            const allTags = item.tags || [];
            const visibleCount = 2;
            const visibleTags = allTags.slice(0, visibleCount);
            const remainingTags = allTags.slice(visibleCount);
            const remainingCount = remainingTags.length;
            const tagBoxSx = {
              px: 1,
              py: '3px',
              bgcolor: 'var(--Fill-Tag-social-tag, #2A2A2A)',
              borderRadius: 20,
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              maxWidth: '100%',
              overflow: 'hidden'
            };
            const tagTypographySx = {
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 12,
              fontWeight: 400,
              fontFamily: 'Inter',
              lineHeight: '12px',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden'
            };
            return (
              <>
                {visibleTags.map((tag) => (
                  <Box key={tag} sx={tagBoxSx}>
                    <Typography sx={tagTypographySx}>{tag}</Typography>
                  </Box>
                ))}
                {remainingCount > 0 && (
                  <DosenTooltip title={remainingTags.join(', ')} placement="top">
                    <Box sx={tagBoxSx}>
                      <Typography sx={tagTypographySx}>+{remainingCount}</Typography>
                    </Box>
                  </DosenTooltip>
                )}
              </>
            );
          })()}
        </Box>
        <Box sx={{ width: 64, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={handleOpenMenu}
            sx={{
              width: 32,
              height: 32,
              color: 'var(--Stroke-solid-white, #FDFDFD)'
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
        {actionsMenu}
      </Box>
    );
  };
  // new version

  // if (!groupTopics.length) {
  //   return (
  //     <Box
  //       sx={{
  //         width: '100%',
  //         height: '100%',
  //         bgcolor: '#1C1C1C',
  //         display: 'flex',
  //         alignItems: 'center',
  //         justifyContent: 'center',
  //         p: 4
  //       }}
  //     >
  //       <Box
  //         sx={{
  //           display: 'flex',
  //           alignItems: 'center',
  //           gap: 1.5,
  //           bgcolor: '#2A2A2A',
  //           borderRadius: 2,
  //           p: 3,
  //           maxWidth: 520
  //         }}
  //       >
  //         <WarningAmberIcon sx={{ color: '#F0B80D', fontSize: 28, flexShrink: 0 }} />
  //         <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontFamily: 'Inter' }}>
  //           Please add at least one topic in <strong style={{ color: '#FDFDFD' }}>Basic Configurations</strong> before creating library resources.
  //         </Typography>
  //       </Box>
  //     </Box>
  //   );
  // }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#1C1C1C',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        // A phone: as tall as its list; the app's scroll area scrolls it.
        [MOBILE_SX]: { height: 'auto', overflow: 'visible', borderRadius: '8px' }
      }}
    >
      <Box
        sx={{
          py: '20px',
          px: '25px ',
          justifyContent: 'space-between',
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          [MOBILE_SX]: { py: '12px', px: '16px' }
        }}
      >
        <Typography color="#FDFDFD" fontFamily="Inter" fontWeight="700">
          Resources
        </Typography>
        <DosenButton sizeVariant="sm" onClick={openAddModal}>
          Add Resource
        </DosenButton>
      </Box>
      <Divider sx={{ borderColor: 'var(--Stroke-table-primary, #2A2A2A)' }} />
      <Box sx={{ flex: 1, overflowY: 'auto', [MOBILE_SX]: { overflowY: 'visible' } }}>
        {/* The table's column heads; a phone's cards label their own figures. */}
        <Box
          sx={{
            alignSelf: 'stretch',
            px: 3,
            py: 1.25,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 3.5,
            width: '100%',
            [MOBILE_SX]: { display: 'none' }
          }}
        >
          <Box sx={{ width: 36, height: 24 }} />
          <Box sx={{ flex: 1, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
            <Typography
              sx={{
                width: '100%',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Resource name
            </Typography>
          </Box>
          <Box
            sx={{ width: 80, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}
          >
            <Typography
              sx={{
                width: '100%',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Upvotes
            </Typography>
          </Box>
          <Box
            sx={{ width: 80, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}
          >
            <Typography
              sx={{
                width: '100%',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Downvotes
            </Typography>
          </Box>
          <Box
            sx={{ width: 192, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Resource type
            </Typography>
          </Box>
          <Box
            sx={{ width: 112, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Read Time
            </Typography>
          </Box>
          <Box
            sx={{ width: 320, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Tags
            </Typography>
          </Box>
          <Box
            sx={{ width: 64, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                textTransform: 'uppercase',
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              Action
            </Typography>
          </Box>
        </Box>
        {articles.map((article) => (
          <ItemList
            key={article.articleId}
            name={article.articleName}
            upVotes={article.upVotes}
            downVotes={article.downVotes}
            contentType={article.type}
            readTime={article.readTime}
            tags={article.tags || []}
            enabled={Boolean(article.isPublished)}
            onPreview={() => handleOpenPreview(article.articleId, article.location)}
            onEdit={() => openEditModal(article)}
            onRemove={() => handleOpenRemove(article)}
            onToggle={(checked) => handleToggleVisibility(article, checked)}
          />
        ))}
      </Box>
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
        fullScreen={isMobileApp}
        PaperProps={{
          sx: {
            background: 'var(--Fill-Table-table-highlight, #151515)',
            borderRadius: 1,
            overflow: 'hidden',
            [MOBILE_SX]: { m: 0, width: '100%', maxWidth: '100%', borderRadius: 0 }
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          <Typography sx={{ color: 'var(--Text-text-primary, #FDFDFD)', fontSize: 18, fontWeight: 700 }}>
            Resource preview
          </Typography>
          <IconButton
            onClick={handleClosePreview}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'var(--Stroke-icon-highlight, #FDFDFD)',
              [MOBILE_SX]: { width: 40, height: 40, top: 10, right: 8 }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {previewLoading ? (
            <Box
              sx={{
                width: '100%',
                minHeight: 320,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                aria-busy="true"
                aria-label="Loading resource"
                sx={{
                  width: '100%',
                  px: 3.5,
                  py: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  alignSelf: 'flex-start'
                }}
              >
                <Bone width={90} height={12} />
                <Bone width="60%" height={22} />
                <Bone width={160} height={14} />
                <Bone height={180} radius="8px" sx={{ mt: 1 }} />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{ px: 3.5, py: 3, display: 'flex', flexDirection: 'column', gap: 3.5, [MOBILE_SX]: { p: '16px', gap: 2.5 } }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography
                    sx={{
                      color: 'var(--Text-text-tertiary, #868686)',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'Inter',
                      lineHeight: '12px'
                    }}
                  >
                    {readTimeLabel}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--Text-text-primary, #FDFDFD)',
                      fontSize: 30,
                      fontWeight: 600,
                      fontFamily: 'Inter',
                      [MOBILE_SX]: { fontSize: 22, lineHeight: 1.3 }
                    }}
                  >
                    {previewData?.articleName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {previewTagDisplay.map((tag) => (
                      <Box
                        key={tag}
                        sx={{
                          px: 1,
                          py: '3px',
                          bgcolor: 'var(--Fill-Tab-tab-default, #2A2A2A)',
                          borderRadius: 20,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'var(--Text-text-primary, #FDFDFD)',
                            fontSize: 12,
                            fontWeight: 400,
                            fontFamily: 'Inter',
                            lineHeight: '12px'
                          }}
                        >
                          {tag}
                        </Typography>
                      </Box>
                    ))}
                    {previewExtraCount > 0 && (
                      <Box
                        sx={{
                          px: 1,
                          py: '3px',
                          bgcolor: 'var(--Fill-Tab-tab-default, #2A2A2A)',
                          borderRadius: 20,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'var(--Text-text-primary, #FDFDFD)',
                            fontSize: 12,
                            fontWeight: 400,
                            fontFamily: 'Inter',
                            lineHeight: '12px'
                          }}
                        >
                          +{previewExtraCount}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      color: 'var(--Text-text-primary, #FDFDFD)',
                      fontSize: 16,
                      fontWeight: 500,
                      fontFamily: 'Inter',
                      lineHeight: '16px'
                    }}
                  >
                    {previewData?.authorName}
                  </Typography>
                </Box>
              </Box>

              {previewData?.type === 'Text' && previewData?.fileUrl && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        border: '2px solid var(--Stroke-icon-dim, #868686)'
                      }}
                    />
                    <Typography
                      sx={{
                        color: 'var(--Stroke-highlight, #F79420)',
                        fontSize: 16,
                        fontWeight: 500,
                        fontFamily: 'Inter'
                      }}
                    >
                      {fileName}
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: '5px',
                        bgcolor: 'var(--Fill-Button-button-gray, #2A2A2A)',
                        borderRadius: 20,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--Stroke-solid-white, #FDFDFD)',
                          fontSize: 12,
                          fontWeight: 500,
                          fontFamily: 'Inter',
                          textTransform: 'uppercase',
                          lineHeight: '12px'
                        }}
                      >
                        {fileTypeLabel || 'FILE'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {previewData?.type === 'Video' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 384,
                      [MOBILE_SX]: { height: 220 },
                      borderRadius: 1,
                      bgcolor: '#0A0A0A',
                      overflow: 'hidden'
                    }}
                  >
                    {previewData?.fileUrl ? (
                      <video controls style={{ width: '100%', height: '100%' }}>
                        <source src={previewData.fileUrl} />
                      </video>
                    ) : null}
                  </Box>
                </Box>
              )}

              {previewData?.type === 'Audio' && (
                <Box
                  sx={{
                    width: '100%',
                    py: 6,
                    borderRadius: 1,
                    bgcolor: '#0A0A0A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ color: 'var(--Text-text-primary, #FDFDFD)', fontSize: 16, fontWeight: 600 }}>
                    Coming soon
                  </Typography>
                </Box>
              )}

              <Typography
                sx={{
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  fontSize: 14,
                  fontWeight: 400,
                  fontFamily: 'Inter',
                  lineHeight: '24px'
                }}
              >
                <RenderHTML htmlContent={previewData?.articleDescription || ''} />
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <AddEditContent
        openForm={openForm}
        handleClickCloseForm={handleCloseForm}
        setOpenForm={(value: boolean) => {
          if (!value) {
            handleCloseForm();
            refreshArticles();
            return;
          }
          setOpenForm(value);
        }}
        selectedActiveId={selectedId || ''}
        singleListData={selectedArticleData}
        action={action}
        setAction={setAction}
        onNewArticleCreated={handleArticleCreated}
        newGroupId={grpId}
      />
      <Dialog
        open={Boolean(removeTarget)}
        onClose={handleCloseRemove}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'var(--Fill-Modal-background, #151515)',
            borderRadius: 2,
            outline: '1px solid var(--Stroke-Modal-border, #2A2A2A)',
            outlineOffset: '-1px',
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          <Typography sx={{ color: 'var(--Text-text-primary, #FDFDFD)', fontSize: 18, fontWeight: 700 }}>
            Remove this resource?
          </Typography>
          <IconButton
            onClick={handleCloseRemove}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'var(--Stroke-icon-highlight, #FDFDFD)',
              [MOBILE_SX]: { width: 40, height: 40, top: 10, right: 8 }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography
              sx={{
                color: 'var(--Text-text-secondary, #B6B6B6)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'Inter',
                lineHeight: '20px',
                textAlign: 'center'
              }}
            >
              Are you sure to remove this resource?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.75 }}>
              <Button
                fullWidth
                onClick={handleCloseRemove}
                sx={{
                  px: 3.5,
                  py: 1.25,
                  bgcolor: 'var(--Fill-Button-button-basic-default, #2A2A2A)',
                  borderRadius: 1,
                  color: 'var(--Stroke-Button-button-basic-default, #FDFDFD)',
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={handleRemove}
                disabled={removeLoading}
                sx={{
                  px: 3.5,
                  py: 1.25,
                  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                  borderRadius: 1,
                  color: 'var(--Stroke-Button-button-cta-default, #FDFDFD)',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
                  },
                  '&.Mui-disabled': buttonDisabledSx
                }}
              >
                {removeLoading ? 'Removing...' : 'Remove'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContentSettings;
